import React, { useState } from 'react';
import { Shield, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import './Auth.css';

interface TwoFactorPageProps {
    userId: string;
    password?: string;
    onSuccess: () => void;
    onBack: () => void;
}

export default function TwoFactorPage({ userId, password, onSuccess, onBack }: TwoFactorPageProps) {
    const { verify2FA } = useApp();
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);

        try {
            await verify2FA(userId, code, password);
            onSuccess();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Invalid code');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setCode(value);
    };

    return (
        <div className="auth-container">
            <div className="auth-card">
                <div className="auth-header">
                    <div className="auth-logo">
                        <Shield size={28} />
                    </div>
                    <h1>Two-Factor Authentication</h1>
                    <p className="auth-2fa-info">
                        We've sent a 6-digit code to your email.<br />
                        Enter it below to continue.
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="auth-form">
                    {error && (
                        <div className="auth-error">
                            <AlertCircle size={16} />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="auth-field">
                        <label htmlFor="code">Verification Code</label>
                        <div className="auth-input-wrapper">
                            <input
                                id="code"
                                type="text"
                                inputMode="numeric"
                                value={code}
                                onChange={handleCodeChange}
                                placeholder="000000"
                                required
                                disabled={isLoading}
                                className="auth-2fa-input"
                                autoComplete="one-time-code"
                            />
                        </div>
                    </div>

                    <button type="submit" className="auth-submit" disabled={isLoading || code.length !== 6}>
                        {isLoading ? (
                            <>
                                <Loader2 size={18} className="spin" />
                                Verifying...
                            </>
                        ) : (
                            'Verify'
                        )}
                    </button>

                    <button type="button" className="auth-link" onClick={onBack}>
                        <ArrowLeft size={14} style={{ marginRight: 4 }} />
                        Back to login
                    </button>
                </form>
            </div>
        </div>
    );
}
